<script setup>
import { ref, computed, reactive, onMounted, onUnmounted } from 'vue';
import { appStorage } from '@shared/utils/storage.js';
import CurriculumLink from "./CurriculumLink.vue";

const { curriculum, currentArticle, filteredForSaved, savedCount, user, openArticle, windowWidth, isSidebarVisible, handleArticleAction } = defineProps({
	curriculum: Array,
	currentArticle: Object,
	currentPage: String,
	savedCount: Number,
	filteredForSaved: Boolean,
	user: Object,
	openArticle: Function,
	windowWidth: Number,
	isSidebarVisible: Boolean,
	handleArticleAction: Function,
});

const collapsedSections = ref([]);
const swipedArticleId = ref(null);
let scrollToActiveTimeout = null;
let setupPending = true;

const curriculumLength = computed(() => {
	return curriculum?.reduce((sum, section) => sum + section.articles.length, 0);
});
const visibleSections = computed(() => filteredForSaved
	? curriculum.filter(section => section.articles.some(article => user?.training?.[article.id]?.saved))
	: curriculum);

defineExpose({ setSwipedArticle, scrollToActive });

function setSwipedArticle(id) { // exposed
	swipedArticleId.value = id;
}

function getRelativeOffsetTop(element, container) {
	let offset = 0;
	while (element && element !== container) {
		offset += element.offsetTop;
		element = element.offsetParent;
	}
	return offset;
}
function scrollToActive() { // exposed
	if (setupPending) { // wait for collapsedSections
		window.setTimeout(() => {
			scrollToActive();
		}, 10);
		return;
	}
	if (scrollToActiveTimeout) return;

	const sidebar = document.getElementById('sidebar');
	const curriculumList = document.getElementById('curriculum');
	var scrollTarget = curriculumList.querySelector('.active');
	var buffer;

	if (!scrollTarget) {
		const newTarget = document.body.querySelector(`[data-id='${user.trainingLastArticle ?? 'a0'}']`);
		newTarget.classList.add('active');
		scrollTarget = newTarget;
	};

	// check if the parent is closed
	const parentEl = scrollTarget.parentElement.parentElement; // <details>
	if (!parentEl.open) {
		scrollTarget = parentEl;
		buffer = (windowWidth >= 1024) ? 54 : 0;
	} else {
		buffer = (windowWidth >= 1024) ? 142 : 110;
	}

	sidebar.scrollTo({
		top: getRelativeOffsetTop(scrollTarget, sidebar) - buffer,
		left: 0,
		behavior: 'instant',
	});
	curriculumList.scrollTo({
		top: getRelativeOffsetTop(scrollTarget, curriculumList) - buffer,
		left: 0,
		behavior: 'instant',
	});
}

let lastWidth = window.innerWidth;
let lastHeight = window.innerHeight;

const handleResize = () => {
	const currentWidth = window.innerWidth;
	const currentHeight = window.innerHeight;

	// Only proceed if width changed OR if height changed by at least 90px
	const widthChanged = currentWidth !== lastWidth;
	const heightChanged = Math.abs(currentHeight - lastHeight) >= 90;

	if (!widthChanged && !heightChanged) return;

	lastWidth = currentWidth;
	lastHeight = currentHeight;

	const sidebar = document.getElementById("sidebar");
	const curriculumList = document.getElementById("curriculum");
	if (!curriculumList || !sidebar || !isSidebarVisible) return;

	scrollToActive();
};

const ax_openLink = (event) => {
	if (event.key === 'Enter') {
		const focusedElement = document.activeElement;
		if (focusedElement && focusedElement.hasAttribute('data-id')) {
			focusedElement.click();
		}
	}
};

function toggleCurriculumSection(event) {
	const sidebar = document.getElementById('sidebar');
	const section = event.currentTarget.parentElement; // <details>
	const sectionOffset = section?.offsetTop;
	const clickedLabel = section.getAttribute('data-section');
	const hasActive = section.querySelector('.active') !== null;

	if (!section.open && hasActive) {
		// opening section
		requestAnimationFrame(() => {
			scrollToActive();
		});
	} else if (section.open && sidebar.scrollTop > sectionOffset) {
		sidebar.scrollTo({
			top: sectionOffset,
			left: 0,
			behavior: 'instant',
		});
	}

	if (collapsedSections?.value.includes(clickedLabel)) {
		collapsedSections.value = collapsedSections.value.filter(label => label !== clickedLabel);
	} else {
		collapsedSections.value.push(clickedLabel);
	}

	appStorage.set('collapsedSections', collapsedSections.value);
}

function getSectionProgress(section) {
	const total = section.articles.length;
	if (!user || !user.training) return 0;

	const readCount = section.articles.filter(article => user.training?.[article.id]?.read).length;
	return Math.round((readCount / total) * 100);
}

const readCount = computed(() => {
	const training = user?.training;

	if (!training) { return; }
	const sum = Object.entries(training).reduce((count, [key, value]) => {
	  // Check that the key looks like an article and value is an object with read: true
	  if (key.startsWith('a') && value.read === true) {
	    return count + 1;
	  }
	  return count;
	}, 0);

	return sum;
})

function setUpCollapsedSections() {
	window.setTimeout(() => {
		const storedCollapsedSections = appStorage.get('collapsedSections') ?? [];
		if (!Array.isArray(storedCollapsedSections)) {
			appStorage.set('collapsedSections', []);
		}
		collapsedSections.value = appStorage.get('collapsedSections') ?? [];
		setupPending = false;
	}, 5);
}

onMounted(() => {
	window.addEventListener("resize", handleResize);
	document.addEventListener("keydown", ax_openLink);
	requestAnimationFrame(() => {
		setUpCollapsedSections();
	})
});

onUnmounted(() => {
	window.removeEventListener("resize", handleResize);
	document.removeEventListener("keydown", ax_openLink);
});
</script>

<template>
<div id="curriculum" class="category-list">
	<template v-for="section in visibleSections" :key="section.label">
	<details
		class="curriculum-section category-list-section"
		:open="!collapsedSections.includes(section.tag)"
		:data-section="section.tag"
	>
		<summary
			@click.prevent="toggleCurriculumSection"
			>
			<h3>{{ section.label }}
				<span v-if="section.id !== 's0'" class="progress">
					({{ getSectionProgress(section) }}%)
				</span>
			</h3>
		</summary>
		<CurriculumLink
			v-for="article in section.articles"
			:key="article.id"
			:article="article"
			:section="section"
			:currentArticle="currentArticle"
			:user="user"
			:openArticle="openArticle"
			:windowWidth="windowWidth"
			:swipedArticleId="swipedArticleId"
			:setSwipedArticle="setSwipedArticle"
			:filteredForSaved="filteredForSaved"
			:handleArticleAction="handleArticleAction"
		/>
	</details>

	<!-- Duplicate active link outside collapsed <details> -->
	<CurriculumLink
		v-if="section.articles.some(article => article.id === currentArticle?.id) && currentArticle?.id !== 'a0'"
		:article="currentArticle"
		:section="section"
		:currentArticle="currentArticle"
		:user="user"
		:openArticle="openArticle"
		:windowWidth="windowWidth"
		:swipedArticleId="swipedArticleId"
		:setSwipedArticle="setSwipedArticle"
		:handleArticleAction="handleArticleAction"
		class="duplicate-active-link"
	/>
	</template>
	<slot name="extra-categories"></slot>
	<footer
		:class="['category-list-footer', filteredForSaved ? 'filtered-buffer' : '']"
		>
		<p>That's all of 'em</p>
		<template v-if="currentPage === 'curriculum'">
			<p>You've read {{ readCount }} of {{ curriculumLength }}</p>
			<progress :value="readCount" :max="curriculumLength" aria-hidden="true"></progress>
		</template>
		<template v-if="currentPage === 'saved'">
			<p>{{ savedCount }} saved</p>
		</template>
	</footer>
</div>
</template>

<style lang='scss' scoped>
.category-list-footer.filtered-buffer { padding-bottom: 174px; }
@media(min-width:1024px) {
	.category-list-footer.filtered-buffer { padding-bottom: 94px; }
}
</style>
